import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, signal, inject } from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ContributionYear} from '../../../model/contribution-year';
import {MonthlyContribution} from '../../../model/contribution-month';
import {LocalizedText} from '../../../model/localized-text';
import {MembershipFee} from '../../../model/membership-fee';
import {MembershipFeeView, toMembershipFeeView} from '../../../view/membership-fee.view';
import {FormatService} from '../../../services/format.service';

@Component({
    selector: 'myabvv-contributions-overview',
    templateUrl: './contributions-overview.component.html',
    styleUrls: ['./contributions-overview.component.css'],
    standalone: true,
    imports: [CommonModule, TranslateModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContributionsOverviewComponent implements OnInit, OnChanges {
  private formatService = inject(FormatService);


  @Input() contributionYears: ContributionYear[];
  @Input() memberShipFees: MembershipFee[] = [];
  // True when pre-euro (Belgian franc) years were filtered out upstream
  @Input() hasHiddenPreEuroYears = false;
  @Input() isLoading: boolean;

  maxSize = 5;
  hasMoreToShow = false;
  lengthMonthArr = new Array(12).fill(null);

  expandedYears = signal(new Set<string>());

  feesView: MembershipFeeView[] = [];
  selectedYear = signal<string | number | null>(null);
  selectedMonth = signal<number | null>(null);
  selectedFees = signal<MembershipFeeView[]>([]);

  ngOnInit() {
    this.initializeHasMoreToShow();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.contributionYears) {
      if (changes.contributionYears.currentValue) {
        if (changes['contributionYears']['currentValue'] !== changes['contributionYears']['previousValue']) {
          this.initializeHasMoreToShow();
          this.autoExpandMostRecentYear();
        }
      }
    }
    if (changes.memberShipFees) {
      this.feesView = (this.memberShipFees ?? []).map(toMembershipFeeView);
    }
  }

  autoExpandMostRecentYear(): void {
    if (this.contributionYears?.length > 0) {
      this.expandedYears.set(new Set([String(this.contributionYears[0].yearOfContribution)]));
    }
  }

  toggleYear(year: string | number): void {
    const key = String(year);
    this.expandedYears.update(current => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
        if (String(this.selectedYear()) === key) {
          this.closeDetails();
        }
      } else {
        next.add(key);
      }
      return next;
    });
  }

  isYearExpanded(year: string | number): boolean {
    return this.expandedYears().has(String(year));
  }

  initializeHasMoreToShow() {
    if (typeof this.contributionYears !== 'undefined' && this.contributionYears !== null) {
      if (this.contributionYears.length > this.maxSize) {
        this.hasMoreToShow = true;
      }
    } else {
      this.hasMoreToShow = false;
    }
  }

  showAll() {
    this.maxSize = this.contributionYears.length;
    this.hasMoreToShow = false;
  }

  onCellClick(year: string | number, month: number, contributions?: MonthlyContribution[]): void {
    if (!this.hasContributionInMonth(contributions, month)) {
      return;
    }

    if (String(this.selectedYear()) === String(year) && this.selectedMonth() === month) {
      this.closeDetails();
      return;
    }

    this.selectedYear.set(year);
    this.selectedMonth.set(month);

    const targetYear = +year;
    this.selectedFees.set(this.feesView.filter(fee => {
      const feeYear = fee.contributionPeriodFromMonthDate?.getFullYear();
      const feeMonth = fee.contributionPeriodFromMonthDate?.getMonth();
      return feeYear === targetYear && feeMonth === (month - 1);
    }));
  }

  isSelected(year: string | number, month: number): boolean {
    return String(this.selectedYear()) === String(year) && this.selectedMonth() === month;
  }

  closeDetails(): void {
    this.selectedYear.set(null);
    this.selectedMonth.set(null);
    this.selectedFees.set([]);
  }

  hasSelectedInYear(year: string | number): boolean {
    return String(this.selectedYear()) === String(year);
  }

  getTranslatedData(array: LocalizedText[]): string {
    return this.formatService.getTranslatedData(array);
  }

  formatAmount(amount: string): string {
    return `${this.formatService.formatCurrency('E')} ${this.formatService.formatNumber(amount)}`;
  }

  hasContributionInMonth(contributions: MonthlyContribution[], month: number): boolean {
    if (!contributions || contributions.length === 0) {
      return false;
    }
    const monthStr = month < 10 ? '0' + month : month.toString();
    return contributions.some(c => c.monthOfContribution?.substr(5, 2) === monthStr);
  }

  getMonthsWithContributions(contributionsByYear: ContributionYear): { month: number; contribution: MonthlyContribution }[] {
    const result: { month: number; contribution: MonthlyContribution }[] = [];
    if (!contributionsByYear?.monthlyContributions) {
      return result;
    }
    for (let month = 1; month <= 12; month++) {
      const monthStr = month < 10 ? '0' + month : month.toString();
      const contribution = contributionsByYear.monthlyContributions.find(
        c => c.monthOfContribution?.substr(5, 2) === monthStr
      );
      if (contribution) {
        result.push({ month, contribution });
      }
    }
    return result;
  }

  hasAnyContributions(contributionsByYear: ContributionYear): boolean {
    return this.getMonthsWithContributions(contributionsByYear).length > 0;
  }
}
